import React, { useState, useMemo } from 'react';
import styles from './ResourcingTable.module.css';
import { CircularProgress } from "@mui/material";

function ResourcingTable({ data, isLoading, error }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Names to exclude from display
  const excludedNames = ['David Coles', 'Christina Carlsson'];

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Memoized sorted data - handle breakdown format with summary and individual rows
  const sortedData = useMemo(() => {
    
    // Handle breakdown format (summary_rows + individual_rows)
    if (data.summary_rows && data.individual_rows) {
      // Filter out excluded names from individual rows
      const filteredIndividualRows = data.individual_rows.filter(row => {
        const name = row[0] || '';
        return !excludedNames.includes(name);
      });
      
      const allRows = [...data.summary_rows, ...filteredIndividualRows];
      
      if (!sortConfig.key) {
        return allRows;
      }

      // Don't sort summary rows (ALL, Blue, Sub-cons), only individual rows
      const summaryRows = data.summary_rows || [];
      const individualRows = [...filteredIndividualRows].sort((a, b) => {
        if (sortConfig.key === 'name') {
          const aVal = a[0] || '';
          const bVal = b[0] || '';
          const comparison = aVal.localeCompare(bVal);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
        return 0;
      });

      return [...summaryRows, ...individualRows];
    }

    // Handle old format (headers/rows) - for backward compatibility
    if (data.rows && data.headers) {
      // Filter out excluded names
      const filteredRows = data.rows.filter(row => {
        const name = row[0] || '';
        return !excludedNames.includes(name);
      });
      
      if (!sortConfig.key) {
        return filteredRows;
      }

      const sortedRows = [...filteredRows].sort((a, b) => {
        if (sortConfig.key === 'name') {
          const aVal = a[0] || '';
          const bVal = b[0] || '';
          const comparison = aVal.localeCompare(bVal);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
        return 0;
      });

      return sortedRows;
    }

    // Handle new format (people/weeks) - for future compatibility
    if (data.people && !sortConfig.key) {
      // Filter out excluded names
      return data.people.filter(person => !excludedNames.includes(person.name));
    }

    if (data.people && sortConfig.key) {
      // Filter out excluded names and then sort
      const filteredPeople = data.people.filter(person => !excludedNames.includes(person.name));
      const sortedPeople = [...filteredPeople].sort((a, b) => {
        if (sortConfig.key === 'name') {
          const comparison = a.name.localeCompare(b.name);
          return sortConfig.direction === 'asc' ? comparison : -comparison;
        }
        return 0;
      });

      return sortedPeople;
    }

    return [];
  }, [data, sortConfig]);

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.loadingContainer}>
          <CircularProgress size={40} />
          <div className={styles.loadingText}>
            Loading resourcing data from Microsoft Planner...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.errorContainer}>
          <div className={styles.errorTitle}>Unable to Load Forecast Data</div>
          <div className={styles.errorMessage}>{error}</div>
        </div>
      </div>
    );
  }

  // No data state - handle all formats
  const hasData = (data.summary_rows && data.summary_rows.length > 0) || 
                  (data.rows && data.rows.length > 0) || 
                  (data.people && data.people.length > 0);
  const hasHeaders = (data.headers && data.headers.length > 0) || (data.weeks && data.weeks.length > 0);

  if (!hasData || !hasHeaders) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.noDataContainer}>
          <div className={styles.noDataIcon}>📅</div>
          <div className={styles.noDataTitle}>No Forecast Data Available</div>
          <div className={styles.noDataMessage}>
            No team member data found for the selected date range and team filter.
          </div>
        </div>
      </div>
    );
  }

  // Get data in appropriate format
  const headers = data.headers || [];
  const weeks = data.weeks || [];
  const summary = data.summary || {};
  const isBreakdownFormat = data.summary_rows && data.individual_rows;
  const isOldFormat = data.headers && data.rows && !isBreakdownFormat;

  return (
    <div className={styles.tableContainer}>

      {/* Table Wrapper for horizontal scrolling */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {isBreakdownFormat || isOldFormat ? (
                // Breakdown format or old format: use headers array
                headers.map((header, index) => {
                  const isNameColumn = index === 0;
                  const isUnscheduledColumn = index === headers.length - 2;
                  const isConstructionColumn = index === headers.length - 1;
                  
                  return (
                    <th 
                      key={index} 
                      className={
                        isNameColumn ? '' : 
                        isUnscheduledColumn ? `${styles.weekColumn} ${styles.unscheduledHeader}` :
                        isConstructionColumn ? `${styles.weekColumn} ${styles.constructionHeader}` :
                        `${styles.weekColumn} ${styles.dateHeader}`
                      }
                    >
                      {isNameColumn ? (
                        <div>
                          {header}
                          <button
                            className={styles.sortButton}
                            onClick={() => handleSort('name')}
                            title="Sort by name"
                          >
                            {sortConfig.key === 'name' 
                              ? (sortConfig.direction === 'asc' ? '↑' : '↓')
                              : '↕'
                            }
                          </button>
                        </div>
                      ) : (
                        header
                      )}
                    </th>
                  );
                })
              ) : (
                // New format: use weeks array
                <>
                  <th>
                    <div>
                      Team Member
                      <button
                        className={styles.sortButton}
                        onClick={() => handleSort('name')}
                        title="Sort by name"
                      >
                        {sortConfig.key === 'name' 
                          ? (sortConfig.direction === 'asc' ? '↑' : '↓')
                          : '↕'
                        }
                      </button>
                    </div>
                  </th>
                  {weeks.map((week, index) => (
                    <th key={index} className={styles.weekColumn}>
                      {week.week_start_date}
                    </th>
                  ))}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isBreakdownFormat ? (
              // Breakdown format: use summary_rows and individual_rows
              <>
              {sortedData.map((row, rowIndex) => {
                const isSummaryRow = data.summary_rows && rowIndex < data.summary_rows.length;
                const rowName = row[0] || '';
                const isAllRow = rowName === 'ALL';
                const isBlueRow = rowName === 'Blue';
                const isSubconsRow = rowName === 'Sub-cons';
                
                // Omit the ALL row; totals will be rendered at the bottom
                if (isSummaryRow && isAllRow) {
                  return null;
                }

                return (
                  <tr 
                    key={rowIndex} 
                    className={
                      isSummaryRow 
                        ? `${styles.summaryRow} ${
                            isAllRow ? styles.allRow : 
                            isBlueRow ? styles.blueRow : 
                            isSubconsRow ? styles.subconsRow : ''
                          }`
                        : styles.individualRow
                    }
                  >
                    {row.map((cell, cellIndex) => {
                      const isNameColumn = cellIndex === 0;
                      const isUnscheduledColumn = cellIndex === row.length - 2;
                      const isConstructionColumn = cellIndex === row.length - 1;
                      const isWeekColumn = cellIndex > 0 && cellIndex < row.length - 2;
                      
                      const cellValue = cell || '';
                      const numericValue = parseFloat(cellValue) || 0;
                      const hasValue = cellValue && cellValue !== '0.00' && cellValue !== '-';
                      
                      return (
                        <td
                          key={cellIndex}
                          className={
                            isNameColumn 
                              ? (isSummaryRow ? styles.summaryNameCell : '') 
                              : `${styles.hoursCell} ${hasValue ? styles.hasHours : ''} ${
                                  isUnscheduledColumn ? styles.unscheduledColumn :
                                  isConstructionColumn ? styles.constructionColumn :
                                  isWeekColumn ? styles.weeklyColumn : ''
                                }`
                          }
                          title={
                            isNameColumn 
                              ? cellValue
                              : hasValue 
                                ? `${cellValue} hours ${
                                    isUnscheduledColumn ? 'unscheduled' :
                                    isConstructionColumn ? 'for construction queries' :
                                    'scheduled'
                                  } for ${rowName}`
                                : `0 hours ${
                                    isUnscheduledColumn ? 'unscheduled' :
                                    isConstructionColumn ? 'for construction queries' :
                                    'scheduled'
                                  }`
                          }
                        >
                          {isNameColumn ? cellValue : hasValue ? cellValue : (cellValue === '' || cellValue === null || cellValue === undefined ? '-' : '0')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Totals row at the bottom (replaces 'ALL' row) */}
              {(() => {
                const teamMemberCount = data.individual_rows.filter(row => {
                  const name = row[0] || '';
                  return !excludedNames.includes(name);
                }).length;
                const allRow = data.summary_rows.find(row => row[0] === 'ALL');
                if (!allRow) return null;

                // Calculate scheduled total (sum of all weekly columns)
                const weekCount = headers.length > 3 ? headers.length - 3 : 0;
                const scheduledTotal = allRow
                  .slice(1, headers.length - 2)
                  .reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
                const unscheduledTotal = parseFloat(allRow[headers.length - 2]) || 0;

                return (
                  <tr className={styles.totalsRow}>
                    {/* First column: headcount + scheduled/unscheduled chips */}
                    <td className={styles.totalsNameCell}>
                      <div className={styles.totalsFirstCell}>
                        <div className={styles.totalsHeadcount}>
                          <span className={styles.totalsHeadcountNumber}>{teamMemberCount}</span>
                          <span className={styles.totalsHeadcountLabel}>Team Members</span>
                        </div>
                        <div className={styles.totalsChips}>
                          <span className={`${styles.totalsChip} ${styles.totalsChipScheduled}`}>Scheduled {Math.round(scheduledTotal)}h</span>
                          <span className={`${styles.totalsChip} ${styles.totalsChipUnscheduled}`}>Unscheduled {Math.round(unscheduledTotal)}h</span>
                        </div>
                      </div>
                    </td>
                    {/* Remaining columns mirror the ALL row values (weekly totals + unscheduled + construction) */}
                    {allRow.slice(1).map((cellValue, idx) => {
                      const value = cellValue || '';
                      const hasValue = value && value !== '0.00' && value !== '-';
                      return (
                        <td key={idx} className={`${styles.hoursCell} ${hasValue ? styles.hasHours : ''}`}>
                          {hasValue ? value : (value === '' || value === null || value === undefined ? '-' : '0')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })()}
              </>
            ) : isOldFormat ? (
              // Old format: use rows array
              sortedData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={cellIndex === 0 ? '' : `${styles.hoursCell} ${cell && cell !== '0.00' ? styles.hasHours : ''}`}
                      title={cellIndex === 0 
                        ? cell 
                        : (cell && cell !== '0.00' ? `${cell} hours scheduled` : '0 hours scheduled')
                      }
                    >
                      {cellIndex === 0 ? cell : (cell && cell !== '0.00' ? `${cell}h` : (cell === '0.00' || parseFloat(cell) === 0 ? '0' : '-'))}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // New format: use people array
              sortedData.map((person) => (
                <tr key={person.name}>
                  <td>{person.name}</td>
                  {weeks.map((week, weekIndex) => {
                    const allocation = person.weekly_allocations?.[weekIndex];
                    const hours = allocation?.total_hours || 0;
                    const hasHours = hours > 0;
                    
                    return (
                      <td
                        key={weekIndex}
                        className={`${styles.hoursCell} ${hasHours ? styles.hasHours : ''}`}
                        title={hasHours 
                          ? `${hours} hours scheduled for ${person.name} on week of ${week.week_start_date}`
                          : '0 hours scheduled'
                        }
                      >
                        {hasHours ? `${hours}h` : '0'}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Section (removed; totals are shown in the last table row) */}
      {false && (() => {
        let teamMemberCount = 0;
        let weekCount = 0;
        let totalHours = 0;
        let avgPerWeek = 0;

        if (isBreakdownFormat) {
          // Count only non-excluded team members
          teamMemberCount = data.individual_rows.filter(row => {
            const name = row[0] || '';
            return !excludedNames.includes(name);
          }).length;
          weekCount = headers.length > 3 ? headers.length - 3 : 0;
          
          // Calculate total hours from the 'ALL' row
          const allRow = data.summary_rows.find(row => row[0] === 'ALL');
          if (allRow) {
            // Sum weekly columns (index 1 to headers.length - 3)
            totalHours = allRow.slice(1, headers.length - 2).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
          }
        } else if (isOldFormat) {
          teamMemberCount = sortedData.length;
          weekCount = headers.length > 1 ? headers.length - 1 : 0;
          totalHours = summary.total_scheduled_hours || 0;
        } else {
          teamMemberCount = sortedData.length;
          weekCount = weeks.length;
          totalHours = summary.total_scheduled_hours || 0;
        }
        
        if (weekCount > 0) {
          avgPerWeek = totalHours / weekCount;
        }

        return (
          <div className={styles.tableSummarySection}>
            <div className={styles.summaryItem}>
              <div className={styles.summaryValue}>
                {teamMemberCount}
              </div>
              <div className={styles.summaryLabel}>
                Team Members
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={styles.summaryValue}>
                {weekCount}
              </div>
              <div className={styles.summaryLabel}>
                Weeks
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={styles.summaryValue}>
                {Math.round(totalHours)}h
              </div>
              <div className={styles.summaryLabel}>
                Total Hours
              </div>
            </div>
            <div className={styles.summaryItem}>
              <div className={styles.summaryValue}>
                {Math.round(avgPerWeek)}h
              </div>
              <div className={styles.summaryLabel}>
                Avg per Week
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default ResourcingTable; 